import React from "react";
import {observer} from "mobx-react-lite";
import "./checkBox.sass";
import classNames from "classnames";
import {BooleanObservable} from "../../../stores/observables/booleanObservable";

export enum CheckBoxType {
  Default = "default",
  Table = "table",
  Muted = "muted"
}

export interface ICheckBoxProps {
  value: boolean | BooleanObservable;
  onValueChange: (value: boolean) => void;
  label?: string | JSX.Element;
  helpText?: string;
  disabled?: boolean;
  clickable?: boolean;
  enableBackground?: boolean;
  type?: CheckBoxType;
  testId?: string;
  invalidErrorMessage?: string;
  isRequired?: boolean;
}

const CheckBox: React.FC<ICheckBoxProps> = ({
  value,
  label,
  onValueChange,
  helpText,
  disabled = false,
  clickable = true,
  enableBackground = false,
  type = CheckBoxType.Default,
  testId,
  invalidErrorMessage,
  isRequired = false
}) => {
  let checkValue = false;
  let isStateValid = null;
  if (value instanceof BooleanObservable) {
    checkValue = value.value;
    isStateValid = value.isValid;
  } else {
    checkValue = value;
  }

  // Ignoring due to onChange causing warnings if not set, label takes care of onChange
  /* istanbul ignore next */
  return (
    <div
      data-testid={testId}
      className={classNames("custom-checkbox-control", {
        disabled,
        "background background-primary": enableBackground && checkValue,
        "background background-default": enableBackground && !checkValue
      })}
      onClick={
        disabled || !clickable
          ? () => {}
          : ($event) => {
              $event.preventDefault(); // This is to prevent event-bubbling
              onValueChange(!checkValue);
            }
      }
      onKeyUp={
        disabled || !clickable
          ? () => {}
          : ($event) => {
              $event.preventDefault();
              if (["", "ENTER"].includes($event.key.trim().toUpperCase())) {
                onValueChange(!checkValue);
              }
            }
      }
    >
      <label
        className={classNames({
          checked: checkValue,
          disabled,
          clickable,
          "positioned-abolute": !!label,
          "type-default": type === CheckBoxType.Default,
          "type-table": type === CheckBoxType.Table,
          "type-muted": type === CheckBoxType.Muted
        })}
      >
        <span className={classNames("label", {clickable})}>
          {label}
          {isRequired && "*"}
        </span>
        {helpText && (
          <span>
            <small>{helpText}</small>
          </span>
        )}
        {!isStateValid && invalidErrorMessage && (
          <span className="error-message">{invalidErrorMessage}</span>
        )}
        <input
          type="checkbox"
          checked={checkValue}
          data-testid={testId + "-checkbox"}
          onChange={() => {}}
        />
        <span
          className={classNames("checkmark", {
            "type-default": type === CheckBoxType.Default,
            "type-table": type === CheckBoxType.Table,
            "type-muted": type === CheckBoxType.Muted,
            clickable,
            error: isStateValid === false
          })}
        />
      </label>
    </div>
  );
};

export default observer(CheckBox);
